import { useMemo, useState } from "react";

import { ChevronRight, Info } from "lucide-react";

import { Chip } from "../../components/ui/primitives";
import {
  brPt,
  gradeColorTexto,
  faixaKey,
  FAIXA_META,
  acessoLabel,
  initials,
  avatarColor,
  engajamentoTone
} from "../../lib/format";
import { cn } from "../../lib/utils";

const TIPOS = [
  ["nota_rede", "Avaliação em Rede"],
  ["nota_avaliacao", "Avaliação"],
  ["nota_regular", "Regular"]
];

export function AlunosTabela({ alunos, onAbrirAluno, disciplinaId }) {
  const [sortKey, setSortKey] = useState("nota");
  const [dir, setDir] = useState(-1);

  const linhas = useMemo(() => {
    const arr = [...alunos];
    arr.sort((a, b) => {
      if (sortKey === "nome") return a.nome.localeCompare(b.nome) * dir;
      const val = r => {
        switch (sortKey) {
          case "nota":
            return r.nota ?? -1;
          case "faixa":
            return -FAIXA_META[faixaKey(r.faixa)].rank;
          case "dias":
            return r.dias_desde_ultimo_acesso ?? 999;
          case "pendentes":
            return r.tarefas_pendentes ?? 0;
          case "engajamento":
            return r.score_engajamento ?? 0;
          default:
            return 0;
        }
      };
      return (val(a) - val(b)) * dir;
    });
    return arr;
  }, [alunos, sortKey, dir]);

  /* O gatilho de ordenação é um <button> de verdade, com `pointerEvents`
     inline. Duas razões:

     1. O AVA declara `.hidden { pointer-events: none; display: none }`
        (_main.scss:14248). O cabeçalho usa `hidden sm:table-header-group`, e o
        Tailwind só sobrescreve o `display` — o `pointer-events: none` do AVA
        sobrevivia e matava o clique. Há uma neutralização no CSS gerado, mas
        depender dela para a interação funcionar é frágil demais: inline não
        depende de nada.
     2. Um <th> com onClick não é alcançável por teclado. Com <button>, é.

     O `extra` (ícone de ajuda) fica FORA do botão, senão clicar nele ordenaria
     a coluna. */
  function th(key, label, num = false, extra) {
    const ativo = sortKey === key;
    return (
      <th
        // eslint-disable-next-line no-nested-ternary
        aria-sort={!ativo ? "none" : dir === 1 ? "ascending" : "descending"}
        className={cn(
          "whitespace-nowrap px-3 py-2.5",
          num ? "text-right" : "text-left"
        )}
        style={{ pointerEvents: "auto" }}
      >
        <span
          className={cn("inline-flex items-center gap-1", num && "justify-end")}
        >
          <button
            type="button"
            style={{ pointerEvents: "auto", cursor: "pointer" }}
            className={cn(
              "inline-flex select-none items-center gap-1 border-0 bg-transparent p-0 text-[10.5px] font-semibold uppercase tracking-[0.06em] transition-colors",
              ativo
                ? "text-[var(--color-ink)]"
                : "text-[var(--color-ink-faint)] hover:text-[var(--color-ink-soft)]"
            )}
            onClick={() => {
              if (ativo) setDir(d => (d === 1 ? -1 : 1));
              else {
                setSortKey(key);
                setDir(key === "nome" ? 1 : -1);
              }
            }}
          >
            {label}
            <span className={ativo ? "opacity-70" : "opacity-30"}>
              {/* eslint-disable-next-line no-nested-ternary */}
              {!ativo ? "▼" : dir === 1 ? "▲" : "▼"}
            </span>
          </button>
          {extra}
        </span>
      </th>
    );
  }

  return (
    <div className="pf-roster overflow-visible">
      <table className="w-full border-collapse text-[12.5px]">
        <thead className="hidden sm:table-header-group">
          <tr className="sm:border-b-2 sm:border-[var(--color-border-strong)]">
            {th("nome", "Aluno")}
            {th("nota", "Nota", true)}
            {th("faixa", "Faixa")}
            {th("dias", "Último acesso")}
            {th("pendentes", "Pendentes", true)}
            {th(
              "engajamento",
              "Engajamento",
              true,
              disciplinaId != null && <EngajamentoDisciplinaAviso />
            )}
          </tr>
        </thead>
        <tbody>
          {linhas.map((r, i) => (
            <LinhaAluno
              key={r.usuario_id}
              r={r}
              i={i}
              onAbrir={() => onAbrirAluno(r)}
            />
          ))}
        </tbody>
      </table>
    </div>
  );
}

function LinhaAluno({ r, i, onAbrir }) {
  const fk = faixaKey(r.faixa);
  const fm = FAIXA_META[fk];
  const acesso = acessoLabel(r.dias_desde_ultimo_acesso);
  const eng = r.score_engajamento;
  const engTone = engajamentoTone(eng);
  const pend = r.tarefas_pendentes ?? 0;

  return (
    <tr
      onClick={onAbrir}
      title="Ver evolução do aluno"
      className={cn(
        // ≥sm: listagem no padrão do AVA — borda de 1px entre linhas, zebra nas
        // ímpares e hover perceptível.
        "cursor-pointer transition-colors sm:table-row sm:border-b sm:border-[var(--color-border)]",
        "sm:odd:bg-[var(--color-surface-2)]/60 sm:hover:bg-[var(--color-brand-dim)]",
        // <sm: card empilhado. Uma borda só — a do card. A separação entre o
        // cabeçalho (nome) e as métricas é feita por ESPAÇO, não por mais uma
        // linha: com borda no card, no nome e entre células o resultado ficava
        // riscado demais.
        "block",
        "mb-3 rounded-[var(--radius-card)] border border-[var(--color-border)] bg-[var(--color-surface)] p-3.5 shadow-[0_1px_2px_rgba(0,0,0,0.04)] sm:mb-0 sm:rounded-none sm:border-x-0 sm:border-t-0 sm:bg-transparent sm:p-0 sm:shadow-none",
        "grid grid-cols-2 gap-x-4 gap-y-3.5 sm:table-row"
      )}
    >
      {/* Nome */}
      <td className="col-span-2 sm:px-3 sm:py-2.5">
        <div className="flex items-center gap-2.5">
          <div
            className="grid h-[30px] w-[30px] flex-none place-items-center rounded-full text-[11px] font-semibold text-white"
            style={{ background: avatarColor(i) }}
          >
            {initials(r.nome)}
          </div>
          <span className="font-medium text-[var(--color-ink)]">{r.nome}</span>
          <ChevronRight
            size={15}
            className="ml-auto flex-none text-[var(--color-ink-faint)] opacity-50"
          />
        </div>
      </td>

      {/* Nota (com tooltip por tipo) */}
      <Celula label="Nota" num>
        {r.nota == null ? (
          <span className="text-[var(--color-ink-faint)]">–</span>
        ) : (
          <span
            className="group/nota relative tnum text-[15px] font-bold"
            style={{ color: gradeColorTexto(r.nota) }}
          >
            {brPt(r.nota)}
            <span
              className={cn(
                "pointer-events-none absolute right-0 z-50 hidden w-52 max-w-[calc(100vw-2rem)] overflow-hidden rounded-xl border border-[var(--color-brand)]/30 bg-[var(--color-surface)] text-left shadow-[var(--shadow-card-hover)] group-hover/nota:block",
                i < 2 ? "top-[calc(100%+8px)]" : "bottom-[calc(100%+8px)]"
              )}
            >
              <span className="block border-b border-[var(--color-brand)]/20 bg-[var(--color-brand-dim)] px-3 py-2 text-[10px] font-semibold uppercase tracking-wide text-[var(--color-brand-dark)]">
                Nota por tipo de atividade
              </span>
              <span className="block p-3">
                {TIPOS.map(([k, lbl]) => {
                  const v = r[k];
                  const ativo =
                    r.nota_tipo ===
                    (k === "nota_rede"
                      ? "rede"
                      : k === "nota_avaliacao"
                      ? "avaliacao"
                      : "regular");
                  return (
                    <span
                      key={k}
                      className="flex items-center justify-between py-0.5 text-[12px]"
                    >
                      <span
                        className={cn(
                          "flex items-center gap-1.5",
                          ativo
                            ? "text-[var(--color-ink)]"
                            : "text-[var(--color-ink-soft)]"
                        )}
                      >
                        <span
                          style={{
                            color: ativo
                              ? "var(--color-brand)"
                              : "var(--color-ink-faint)"
                          }}
                        >
                          {ativo ? "●" : "○"}
                        </span>
                        {lbl}
                      </span>
                      <span className="tnum font-medium">
                        {v == null ? "–" : brPt(v)}
                      </span>
                    </span>
                  );
                })}
              </span>
            </span>
          </span>
        )}
      </Celula>

      {/* Faixa */}
      <Celula label="Faixa" className="col-span-2 sm:col-span-1">
        <Chip tone={fm.cls}>{fm.label}</Chip>
      </Celula>

      {/* Último acesso */}
      <Celula label="Último acesso">
        <span
          className="text-[13px]"
          style={{
            color:
              acesso.tone === "bad"
                ? "var(--color-bad)"
                : acesso.tone === "warn"
                ? "var(--color-warn)"
                : "var(--color-ink-soft)"
          }}
        >
          {acesso.text}
        </span>
      </Celula>

      {/* Pendentes */}
      <Celula label="Pendentes" num>
        <span
          className="tnum inline-grid h-[26px] min-w-[26px] place-items-center rounded-md px-1.5 text-[13px] font-semibold"
          style={
            pend >= 4
              ? // -ink porque o número fica sobre o fundo tonal vermelho, em
                // 13px semibold — ver o bloco de status em tokens.css
                {
                  background: "var(--color-bad-bg)",
                  color: "var(--color-bad-ink)"
                }
              : {
                  background: "var(--color-surface-3)",
                  color: "var(--color-ink-soft)"
                }
          }
        >
          {pend}
        </span>
      </Celula>

      {/* Engajamento */}
      <Celula label="Engajamento" num>
        {eng == null ? (
          <span className="text-[var(--color-ink-faint)]">–</span>
        ) : (
          <EngajamentoChip aluno={r} tone={engTone} rowIndex={i} />
        )}
      </Celula>
    </tr>
  );
}

/** Aviso no cabeçalho: com filtro de disciplina ativo, Engajamento continua
 * sempre da TURMA INTEIRA — o Score de Engajamento não tem grão de disciplina
 * (fato_score_engajamento_dia não carrega disciplina_id), diferente de Tempo
 * gasto/Pendentes, que recortam de verdade pela disciplina escolhida. */
function EngajamentoDisciplinaAviso() {
  return (
    <span
      className="group/avisoeng relative inline-flex normal-case tracking-normal"
      onClick={e => e.stopPropagation()}
    >
      <Info size={12} className="text-[var(--color-ink-faint)]" />
      <span className="pointer-events-none absolute left-1/2 top-[calc(100%+8px)] z-50 hidden w-56 max-w-[calc(100vw-2rem)] -translate-x-1/2 rounded-xl border border-[var(--color-brand)]/30 bg-[var(--color-surface)] p-2.5 text-left text-[11px] font-normal leading-snug normal-case text-[var(--color-ink-soft)] shadow-[var(--shadow-card-hover)] group-hover/avisoeng:block">
        Sempre da turma inteira — o Score de Engajamento não é calculado por
        disciplina.
      </span>
    </span>
  );
}

function EngajamentoChip({ aluno, tone, rowIndex }) {
  const pilares = [
    ["Progresso", aluno.eng_progresso],
    ["Qualidade de tempo", aluno.eng_qualidade],
    ["Pontualidade", aluno.eng_pontualidade],
    ["Exploração", aluno.eng_exploracao]
  ];

  return (
    <span className="group/eng relative inline-flex">
      <Chip tone={tone}>{Math.round(aluno.score_engajamento ?? 0)}%</Chip>
      <span
        className={cn(
          "pointer-events-none absolute right-0 z-50 hidden w-64 max-w-[calc(100vw-2rem)] overflow-hidden rounded-xl border border-[var(--color-brand)]/30 bg-[var(--color-surface)] text-left shadow-[var(--shadow-card-hover)] group-hover/eng:block",
          rowIndex < 2 ? "top-[calc(100%+9px)]" : "bottom-[calc(100%+9px)]"
        )}
      >
        <span className="block border-b border-[var(--color-brand)]/20 bg-[var(--color-brand-dim)] px-3 py-2 text-[10.5px] font-semibold uppercase tracking-wide text-[var(--color-brand-dark)]">
          Pilares do engajamento
        </span>
        <span className="grid gap-2 p-3">
          {pilares.map(([label, value]) => {
            const pct = Math.max(0, Math.min(100, Number(value) || 0));
            return (
              <span key={label} className="block">
                <span className="mb-1 flex items-baseline justify-between gap-3">
                  <span className="text-[12px] text-[var(--color-ink-soft)]">
                    {label}
                  </span>
                  <span className="tnum text-[12px] font-semibold text-[var(--color-ink)]">
                    {Math.round(pct)}%
                  </span>
                </span>
                <span className="block h-1.5 overflow-hidden rounded-full bg-[var(--color-surface-3)]">
                  <span
                    className="block h-full rounded-full bg-[var(--color-data)]"
                    style={{ width: `${pct}%` }}
                  />
                </span>
              </span>
            );
          })}
        </span>
      </span>
    </span>
  );
}

/** Célula que vira "rótulo + valor" no modo card (mobile) e td normal no desktop. */
function Celula({ label, num, className, children }) {
  return (
    <td
      className={cn("sm:px-3 sm:py-2.5", num ? "sm:text-right" : "", className)}
    >
      <span className="mb-1 block text-[9.5px] font-semibold uppercase tracking-[0.06em] text-[var(--color-ink-faint)] sm:hidden">
        {label}
      </span>
      {children}
    </td>
  );
}
